import type { ReactNode } from 'react';

import { Badge, type BadgeTone } from '@/shared/ui/badge';
import type { EntitySelectorHandle } from '@/shared/ui/async-selector';

import type { ListColumnDef, ListDateRangePreset, ListSelectorFilterDef } from './types';

const MONEY_FORMAT = new Intl.NumberFormat('cs-CZ', {
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
});
const DATE_FORMAT = new Intl.DateTimeFormat('cs-CZ', {
  day: 'numeric',
  month: 'numeric',
  year: 'numeric',
});
const DATE_TIME_FORMAT = new Intl.DateTimeFormat('cs-CZ', {
  day: '2-digit',
  month: '2-digit',
  year: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
});

function dash(): ReactNode {
  return <span className="text-phx-text-faint">—</span>;
}

function parseDate(value: string | null | undefined): Date | null {
  if (!value) {
    return null;
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

type ColumnOptions<TItem> = Omit<ListColumnDef<TItem>, 'key' | 'cell'>;
type ValueKey<TItem, TValue> = {
  [K in keyof TItem & string]: TItem[K] extends TValue ? K : never;
}[keyof TItem & string];

function make<TItem>(
  key: string,
  options: ColumnOptions<TItem>,
  cell: (item: TItem) => ReactNode
): ListColumnDef<TItem> {
  return { key, ...options, cell };
}

/**
 * Typované helpery sloupců — volba rendereru buňky dle anatomie 03 §3. Klíč sloupce je
 * `keyof TItem`, takže překlep v názvu pole DTO neprojde kompilací.
 */
export const col = {
  /** Prostý text. */
  text<TItem>(key: ValueKey<TItem, string | null | undefined>, options: ColumnOptions<TItem>) {
    return make<TItem>(key, options, (item) => {
      const value = item[key as keyof TItem] as string | null | undefined;
      return value ? value : dash();
    });
  },

  /** Mono font — čísla záznamů, kódy. */
  mono<TItem>(
    key: ValueKey<TItem, string | number | null | undefined>,
    options: ColumnOptions<TItem>
  ) {
    return make<TItem>(key, options, (item) => {
      const value = item[key as keyof TItem] as string | number | null | undefined;
      return value === null || value === undefined ? (
        dash()
      ) : (
        <span className="font-mono text-[11.5px]">{String(value)}</span>
      );
    });
  },

  /**
   * Zvýrazněný identifikační sloupec — buňka je odkaz otevírající záznam (náhledový drawer /
   * `onOpen`). Samotné otevření řídí engine v tabulce; zde stačí `link: true` a tučný text.
   */
  strong<TItem>(key: ValueKey<TItem, string | null | undefined>, options: ColumnOptions<TItem>) {
    return make<TItem>(key, { link: true, ...options }, (item) => {
      const value = item[key as keyof TItem] as string | null | undefined;
      return value ? <span className="font-semibold">{value}</span> : dash();
    });
  },

  /** Tabulární číslice (počty). */
  num<TItem>(key: ValueKey<TItem, number | null | undefined>, options: ColumnOptions<TItem>) {
    return make<TItem>(key, { align: 'right', ...options }, (item) => {
      const value = item[key as keyof TItem] as number | null | undefined;
      return value === null || value === undefined ? (
        dash()
      ) : (
        <span className="tabular-nums">{value}</span>
      );
    });
  },

  /** Peníze — tabulární číslice, tenké mezery, volitelný kód měny muted za hodnotou. */
  money<TItem>(
    key: ValueKey<TItem, number | null | undefined>,
    options: ColumnOptions<TItem> & { unit?: (item: TItem) => string | null | undefined }
  ) {
    const { unit, ...rest } = options;
    return make<TItem>(key, { align: 'right', ...rest }, (item) => {
      const value = item[key as keyof TItem] as number | null | undefined;
      if (!value) {
        return dash();
      }
      const currency = unit?.(item);
      return (
        <span className="tabular-nums">
          {MONEY_FORMAT.format(value)}
          {currency ? (
            <span className="text-phx-text-muted ml-phx-2 text-phx-xs">{currency}</span>
          ) : null}
        </span>
      );
    });
  },

  /** Datum (bez času). */
  date<TItem>(key: ValueKey<TItem, string | null | undefined>, options: ColumnOptions<TItem>) {
    return make<TItem>(key, options, (item) => {
      const date = parseDate(item[key as keyof TItem] as string | null | undefined);
      return date ? <span className="tabular-nums">{DATE_FORMAT.format(date)}</span> : dash();
    });
  },

  /** Datum a čas. */
  dateTime<TItem>(key: ValueKey<TItem, string | null | undefined>, options: ColumnOptions<TItem>) {
    return make<TItem>(key, options, (item) => {
      const date = parseDate(item[key as keyof TItem] as string | null | undefined);
      return date ? <span className="tabular-nums">{DATE_TIME_FORMAT.format(date)}</span> : dash();
    });
  },

  /** Stavová pilulka (soft badge) s tónem dle záznamu. */
  badge<TItem>(
    key: ValueKey<TItem, string | null | undefined>,
    options: ColumnOptions<TItem> & { tone?: (item: TItem) => BadgeTone }
  ) {
    const { tone, ...rest } = options;
    return make<TItem>(key, rest, (item) => {
      const value = item[key as keyof TItem] as string | null | undefined;
      if (!value) {
        return dash();
      }
      return <Badge tone={tone?.(item) ?? 'neutral'}>{value}</Badge>;
    });
  },

  /** Volný text v jednom řádku s ellipsis; plné znění v tooltipu. */
  note<TItem>(key: ValueKey<TItem, string | null | undefined>, options: ColumnOptions<TItem>) {
    return make<TItem>(key, options, (item) => {
      const value = item[key as keyof TItem] as string | null | undefined;
      if (!value) {
        return dash();
      }
      return (
        <div className="phxlist-note" style={{ width: (options.w || 200) - 20 }} title={value}>
          {value}
        </div>
      );
    });
  },

  /** Vlastní renderer buňky. */
  custom<TItem>(definition: ListColumnDef<TItem>) {
    return definition;
  },
};

/** Typované helpery filtračních polí — určují editor, operátory a souhrn na chipu (01 §5). */
export const filter = {
  enum: <TOptions,>(key: string, options: TOptions & { label: string; group: string }) => ({
    type: 'enum' as const,
    key,
    ...options,
  }),
  /**
   * Enum filtr nad registrem editovaný entity selectorem (`defineEntityAsyncSelector`).
   * Hodnota drží `ids` + celé *Ref modely (`items`) — chipy se po načtení pohledu vykreslí
   * okamžitě; resolveByIds slouží jen k background revalidaci labelů.
   */
  selector: <TItem,>(
    key: string,
    options: {
      label: string;
      group: string;
      freq?: boolean;
      selector: EntitySelectorHandle<TItem>;
      getId: (item: TItem) => string | number;
      getLabel: (item: TItem) => string;
    }
  ): ListSelectorFilterDef<TItem> => ({
    type: 'selector' as const,
    key,
    ...options,
  }),
  text: (key: string, options: { label: string; group: string; freq?: boolean }) => ({
    type: 'text' as const,
    key,
    ...options,
  }),
  number: (
    key: string,
    options: { label: string; group: string; freq?: boolean; unit?: string }
  ) => ({
    type: 'number' as const,
    key,
    ...options,
  }),
  daterange: (
    key: string,
    options: { label: string; group: string; freq?: boolean; presets?: ListDateRangePreset[] }
  ) => ({
    type: 'daterange' as const,
    key,
    ...options,
  }),
};
